import { CheckIcon, PencilIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { patchSettingsAccount } from '../../../../api/settingsAccount';
import { States } from '../../../../constants';
import { ReactQueryKey } from '../../../../constants/ReactQueryKeys';
import type { ClientDetailsInterface } from '../../../../pages/SageAdmin/Clients/Details/types';
import { authSelector } from '../../../../store';
import { notifyUser } from '../../../../store/Toast';
import type { State } from '../../../../types/Constants';
import { sanitizeResponseError } from '../../../../utils';
import { zipCodeRegEx } from '../../../../utils/RegularExpressions';
import { HasPermission } from '../../../Permission/HasPermission';
import { HookForm } from '../../../UI/HookForm/Form';
import { HookInput } from '../../../UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '../../../UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '../../../UI/HookForm/Provider';

const validationSchema = Yup.object({
  address: Yup.string().required('Required'),
  city: Yup.string().required('Required'),
  state: Yup.object({
    name: Yup.string(),
    abbreviation: Yup.string(),
  }).nullable(),
  zip: Yup.string().matches(zipCodeRegEx, 'Valid Zip / Postal Code is required'),
});

export interface CompanyAddressProps {
  company: Partial<ClientDetailsInterface>;
}

const CompanyAddressEditableForm = (props: CompanyAddressProps) => {
  const queryClient = useQueryClient();
  const { data: currentUserData } = useSelector(authSelector);
  const { accountId, isSuper } = currentUserData;
  const { address, city, state, country, zip } = props.company;
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const dispatch = useDispatch();

  const { reset } = useFormContext();

  const defaultValues = {
    address,
    city,
    state: { name: state, abbreviation: state },
    country,
    zip,
  };

  const postCompanyAddress = useMutation(
    (data: Partial<ClientDetailsInterface>) => patchSettingsAccount({ data, accountId, isSystemAdmin: isSuper }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ReactQueryKey.GET_CLIENT_ACCOUNT]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Successfully updated Address',
            toastType: 'success',
          })
        );
        toggleEdit();
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const toggleEdit = () => {
    reset(defaultValues);
    setIsEdit((prev) => !prev);
  };
  const onSubmit = ({ state, ...rest }: Partial<Omit<ClientDetailsInterface, 'state'>> & { state: State }) => {
    postCompanyAddress.mutate({
      ...rest,
      state: state.name,
    });
  };

  return (
    <HookForm onSubmit={onSubmit} className='flex justify-between '>
      {isEdit ? (
        <div className='space-y-4'>
          <HookInput type='text' label='Address' name='address' disabled={postCompanyAddress.isLoading} />
          <HookInput type='text' label='City' name='city' disabled={postCompanyAddress.isLoading} />
          <div className='grid grid-cols-5 gap-4'>
            <div className='col-span-3'>
              <HookReactSingleSelect
                id='state'
                options={States}
                getOptionLabel={(option) => (option as State).name}
                getOptionValue={(option) => (option as State).abbreviation}
                placeholder='Select State'
                name='state'
                label='State'
              />
            </div>
            <HookInput
              customClassName='col-span-2'
              type='number'
              label='Zip / Postal Code'
              name='zip'
              disabled={postCompanyAddress.isLoading}
            />
          </div>
        </div>
      ) : (
        <span className='flex-grow'>
          {address}, {city}, {state}, {country}, {zip}
        </span>
      )}
      {
        <HasPermission of='UPDATE_COMPANY_PROFILE'>
          <span className='ml-4 shrink-0'>
            {!isEdit ? (
              <button
                type='button'
                key='update'
                className='bg-white rounded-md font-medium text-neutralColor-light hover:text-primaryColor-normal'
                onClick={toggleEdit}
              >
                <PencilIcon className='h-5 w-5' />
              </button>
            ) : (
              <button
                type='submit'
                key='submit'
                disabled={postCompanyAddress.isLoading}
                className='bg-white rounded-md font-medium text-primaryColor-normal'
              >
                {postCompanyAddress.isLoading ? (
                  'Saving...'
                ) : (
                  <div className='flex gap-2'>
                    <div className='bg-neutralColor-lightest hover:bg-neutralColor-lighter rounded-full p-1'>
                      <CheckIcon className='h-5 w-5' />
                    </div>
                    <div
                      className='p-1 rounded-full bg-neutralColor-lightest hover:bg-neutralColor-lighter cursor-pointer'
                      onClick={toggleEdit}
                    >
                      <XCircleIcon className='h-5 w-5 text-tertiaryColor-light hover:text-tertiaryColor-normal' />
                    </div>
                  </div>
                )}
              </button>
            )}
          </span>
        </HasPermission>
      }
    </HookForm>
  );
};

export const CompanyAddress = (props: CompanyAddressProps) => {
  const { address, city, state, country, zip } = props.company;

  const transformedData = {
    address,
    city,
    state: { name: state, abbreviation: state },
    country,
    zip,
  };

  return (
    <HookFormProvider defaultValues={transformedData} resolver={yupResolver(validationSchema) as any}>
      <CompanyAddressEditableForm {...props} />
    </HookFormProvider>
  );
};
